import {DocumentContext, DocumentEntityType} from '@sollapay/enums';
import {ContentSection, CountBadge, EmptyState, Illustration} from '@sollapay/ui/components';
import {SecureDocumentIllustration} from '@sollapay/ui/illustrations';
import {formatFileSize, toISO} from '@sollapay/utils';
import {memo, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {useRefreshBuyerDetail} from '@/features/buyers/hooks/useRefreshBuyerDetail';
import {AddDocumentsButton, DocumentsTable} from '@/features/documents';
import {
  useTrustAccountPermissions,
  useTrustTypeStrategy
} from '@/features/trust-accounts/providers';

import {buildPurchaseDocumentTabs} from '../utils';

import type {DocumentTableRow} from '@/features/documents';
import type {DocumentListItemRawDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface PurchaseDocumentsSectionProps {
  documents: DocumentListItemRawDTO[];
  identityDocuments: DocumentListItemRawDTO[];
  trustAccountId: string;
  buyerId: string;
  purchaseId: string;
}

function mapToRow(
  doc: DocumentListItemRawDTO,
  documentContext?: DocumentContext
): DocumentTableRow {
  return {
    id: doc.id,
    fileName: doc.originalFileName ?? doc.fileName,
    fileExtension: doc.fileName.split('.').pop() ?? '',
    fileSize: formatFileSize(doc.fileSize),
    documentType: doc.documentType,
    uploadDate: toISO(doc.uploadedAt) || toISO(doc.createdAt),
    lastUpdated: toISO(doc.updatedAt),
    documentContext
  };
}

export const PurchaseDocumentsSection: FC<PurchaseDocumentsSectionProps> = memo(
  function PurchaseDocumentsSection({
    documents,
    identityDocuments,
    trustAccountId,
    buyerId,
    purchaseId
  }) {
    const {t} = useTranslation(['trustAccounts', 'common']);
    const {canUploadDocuments, canDeleteDocuments} = useTrustAccountPermissions();
    const strategy = useTrustTypeStrategy();
    const refreshBuyer = useRefreshBuyerDetail(trustAccountId, buyerId);

    // ID scans live in the KYC context, so their rows download/delete through it.
    const rows = useMemo(
      () => [
        ...identityDocuments.map(doc => mapToRow(doc, DocumentContext.KYC)),
        ...documents.map(doc => mapToRow(doc))
      ],
      [documents, identityDocuments]
    );

    const uploadTabs = buildPurchaseDocumentTabs(strategy, t);

    return (
      <ContentSection
        title={t('trustAccounts:view.purchases.documents')}
        titleBadge={<CountBadge count={rows.length} />}
        action={
          canUploadDocuments && (
            <AddDocumentsButton
              title={t('common:addDocuments')}
              entityType={DocumentEntityType.TRUST_ACCOUNT}
              entityId={trustAccountId}
              documentContext={DocumentContext.PURCHASE_DOCUMENTS}
              tabs={uploadTabs}
              defaultValue={uploadTabs[0]?.value}
              metadata={{purchaseId}}
              metadataFilter={{purchaseId}}
              onDocumentUploaded={refreshBuyer}
              onDocumentChange={refreshBuyer}
            />
          )
        }
      >
        <DocumentsTable
          documents={rows}
          entityType={DocumentEntityType.TRUST_ACCOUNT}
          entityId={trustAccountId}
          documentContext={DocumentContext.PURCHASE_DOCUMENTS}
          hiddenColumns={['uploadedBy']}
          emptyState={
            <EmptyState
              className="py-8"
              illustration={
                <Illustration width={64} height={64}>
                  <SecureDocumentIllustration />
                </Illustration>
              }
              title={t('trustAccounts:view.purchases.emptyDocuments.title')}
              subtitle={t('trustAccounts:view.purchases.emptyDocuments.description')}
            />
          }
          onDeleted={canDeleteDocuments ? refreshBuyer : undefined}
        />
      </ContentSection>
    );
  }
);
